import { cn } from '@/lib/utils';
import { Button } from '@ui/button';
import { Card, CardContent } from '@ui/authcard';
import { Input } from '@ui/input';
import { Label } from '@ui/label';
import { Tooltip, TooltipTrigger, TooltipContent } from '@ui/tooltip';
import { ChevronDownIcon, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import {
  validateMyanmarPhoneNumber,
  formatPhoneNumberDisplay,
  toApiFormat,
} from '../../utils/phoneValidation';
import { logger } from '../../utils/logger';

interface LoginFormProps extends React.ComponentProps<'div'> {
  onSuccess?: () => void;
}

export function LoginForm({ className, onSuccess, ...props }: LoginFormProps) {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneValidationError, setPhoneValidationError] = useState('');
  const [emailValidationError, setEmailValidationError] = useState('');
  const [loginMode, setLoginMode] = useState<'phone' | 'email'>('phone');
  const { loginWithCredentials, sendOTP } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const validatePhone = (value: string) => {
    if (value.trim()) {
      const validation = validateMyanmarPhoneNumber(value);
      if (!validation.isValid) {
        setPhoneValidationError('Please enter a valid Myanmar phone number');
      } else {
        setPhoneValidationError('');
      }
      return formatPhoneNumberDisplay(value);
    } else {
      setPhoneValidationError('');
      return value;
    }
  };

  const validateEmail = (value: string) => {
    if (value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setEmailValidationError('Please enter a valid email address');
      } else {
        setEmailValidationError('');
      }
    } else {
      setEmailValidationError('');
    }
    return value;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const processed = validatePhone(value);
    setPhoneNumber(processed);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const processed = validateEmail(value);
    setEmail(processed);
  };

  // const handleForgotPassword = () => {
  //   setError('');

  //   // Get current identifier based on login mode
  //   const currentIdentifier = loginMode === 'phone' ? phoneNumber : email;

  //   // Validate identifier
  //   if (!currentIdentifier.trim()) {
  //     if (loginMode === 'phone') {
  //       setPhoneValidationError('Please enter your phone number');
  //     } else {
  //       setEmailValidationError('Please enter your email address');
  //     }
  //     return;
  //   }

  //   // Validate based on type
  //   if (loginMode === 'phone') {
  //     const validation = validateMyanmarPhoneNumber(phoneNumber);
  //     if (!validation.isValid) {
  //       setPhoneValidationError(validation.error || 'Please enter a valid phone number');
  //       return;
  //     }
  //   }

  //   // Navigate to forgot password with the identifier
  //   navigate('/forgot-password', {
  //     state: {
  //       identifier: currentIdentifier,
  //       isEmail: loginMode === 'email'
  //     }
  //   });
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Get current identifier based on login mode
    const currentIdentifier = loginMode === 'phone' ? phoneNumber : email;

    // Validate identifier
    if (!currentIdentifier.trim()) {
      if (loginMode === 'phone') {
        setPhoneValidationError('Please enter your phone number');
      } else {
        setEmailValidationError('Please enter your email address');
      }
      return;
    }

    // Validate based on type
    if (loginMode === 'phone') {
      const validation = validateMyanmarPhoneNumber(phoneNumber);
      if (!validation.isValid) {
        setPhoneValidationError(validation.error || 'Please enter a valid phone number');
        return;
      }
    }

    setIsLoading(true);

    try {
      let loginData: { phoneNumber?: string; email?: string; password: string };

      if (loginMode === 'email') {
        loginData = {
          email: email,
          password: password,
        };
      } else {
        const apiPhoneNumber = toApiFormat(phoneNumber);
        loginData = {
          phoneNumber: apiPhoneNumber,
          password: password,
        };
      }

      const response = await loginWithCredentials(loginData);

      if (response.success) {
        if (response.message) {
          showToast('success', response.message);
        }
        // Wait for state to fully update before calling success callback
        await new Promise((resolve) => setTimeout(resolve, 500));

        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/');
        }
      } else {
        logger.log('Login failed, full response:', JSON.stringify(response, null, 2));

        // Check if it's a phone verification required error
        // Check both response.error and response.message for the error text
        const errorMessage = response.error || response.message || '';
        logger.log('Checking error message:', errorMessage);
        logger.log('Error message type:', typeof errorMessage);
        logger.log('Exact error check:', errorMessage === 'Phone verification required');
        logger.log(
          'Case insensitive check:',
          errorMessage.toLowerCase().includes('phone verification required'),
        );

        if (
          errorMessage === 'Phone verification required' ||
          errorMessage.toLowerCase().includes('phone verification required')
        ) {
          logger.log('Phone verification required detected, sending OTP...');

          // Only send OTP if login was attempted with phone number
          if (loginMode === 'phone') {
            try {
              const apiPhoneNumber = toApiFormat(phoneNumber);
              const otpResponse = await sendOTP({
                phoneNumber: apiPhoneNumber,
              });

              logger.log('OTP response:', JSON.stringify(otpResponse, null, 2));

              if (otpResponse.success) {
                logger.log('OTP sent successfully, redirecting to verify-phone page');
                if (otpResponse.message) {
                  showToast('success', otpResponse.message);
                }
                // Redirect to OTP verification page with phone number
                navigate(`/verify-phone?phone=${apiPhoneNumber}`);
                // Close the dialog after successful OTP sending
                if (onSuccess) {
                  onSuccess();
                }
              } else {
                logger.log('OTP send failed:', otpResponse);
                if (otpResponse.message) {
                  setError(otpResponse.message);
                  showToast('error', otpResponse.message);
                }
              }
            } catch (otpError) {
              logger.error('OTP send error:', otpError);
              if (
                otpError &&
                typeof otpError === 'object' &&
                'message' in otpError &&
                typeof otpError.message === 'string'
              ) {
                setError(otpError.message);
                showToast('error', otpError.message);
              }
            }
          } else {
            // Email login with phone verification required - no toast, let API handle messaging
          }
        } else if (
          errorMessage === 'Email verification required' ||
          errorMessage.toLowerCase().includes('email verification required')
        ) {
          logger.log('Email verification required detected, sending OTP...');

          // Only send OTP if login was attempted with email
          if (loginMode === 'email') {
            try {
              const otpResponse = await sendOTP({
                email: email,
              });

              logger.log('OTP response:', JSON.stringify(otpResponse, null, 2));

              if (otpResponse.success) {
                logger.log('OTP sent successfully, redirecting to verify-email page');
                if (otpResponse.message) {
                  showToast('success', otpResponse.message);
                }
                // Redirect to OTP verification page with email
                navigate(`/verify-email?email=${encodeURIComponent(email)}`);
                // Close the dialog after successful OTP sending
                if (onSuccess) {
                  onSuccess();
                }
              } else {
                logger.log('OTP send failed:', otpResponse);
                if (otpResponse.message) {
                  setError(otpResponse.message);
                  showToast('error', otpResponse.message);
                }
              }
            } catch (otpError) {
              logger.error('OTP send error:', otpError);
              if (
                otpError &&
                typeof otpError === 'object' &&
                'message' in otpError &&
                typeof otpError.message === 'string'
              ) {
                setError(otpError.message);
                showToast('error', otpError.message);
              }
            }
          } else {
            // Phone login with email verification required - no toast, let API handle messaging
          }
        } else {
          // Display the exact error message from API response
          if (errorMessage) {
            setError(errorMessage);
            showToast('error', errorMessage);
          }
        }
      }
    } catch (error) {
      logger.error('Login error:', error);
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        setError(error.message);
        showToast('error', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden py-0">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              {/* <div className="flex flex-col items-center text-center">
                <h1 className="text-2xl font-bold">{t('pages.welcome_back')}</h1>
                <p className="text-balance text-muted-foreground">{t('pages.login_description')}</p>
              </div> */}
              {error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-600 border border-red-200">
                  {error}
                </div>
              )}
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="identifier">
                    {loginMode === 'phone' ? t('auth.phone') : t('auth.email')}
                  </Label>
                  <div className="flex rounded-md border border-input">
                    <button
                      type="button"
                      onClick={() => setLoginMode('phone')}
                      className={`px-3 py-1 text-xs rounded-l-md ${
                        loginMode === 'phone'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      Phone
                    </button>
                    <button
                      type="button"
                      onClick={() => setLoginMode('email')}
                      className={`px-3 py-1 text-xs rounded-r-md ${
                        loginMode === 'email'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      Email
                    </button>
                  </div>
                </div>
                <div className="flex rounded-md">
                  {loginMode === 'phone' && (
                    <div className="grid shrink-0 grid-cols-1 focus-within:relative">
                      <select
                        id="country"
                        name="country"
                        autoComplete="country"
                        aria-label="Country"
                        disabled
                        defaultValue="+95"
                        className="col-start-1 row-start-1 w-full appearance-none rounded-l-md border border-input bg-background py-1.5 pr-7 pl-3 text-sm text-muted-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      >
                        <option>+95</option>
                      </select>
                      <ChevronDownIcon
                        aria-hidden="true"
                        className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end text-muted-foreground"
                      />
                    </div>
                  )}
                  {loginMode === 'phone' ? (
                    <Tooltip open={!!phoneValidationError}>
                      <TooltipTrigger asChild>
                        <Input
                          id="phone"
                          type="tel"
                          value={phoneNumber}
                          onChange={handlePhoneChange}
                          placeholder="9 xxx xxxxxx"
                          className="rounded-l-none border-l-0 focus:border-l focus:ring-offset-0"
                        />
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <div className="flex items-center gap-2">
                          <span className="text-red-600">⚠</span>
                          <span>{phoneValidationError}</span>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip open={!!emailValidationError}>
                      <TooltipTrigger asChild>
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={handleEmailChange}
                          placeholder="your@email.com"
                          className="rounded-md"
                        />
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <div className="flex items-center gap-2">
                          <span className="text-red-600">⚠</span>
                          <span>{emailValidationError}</span>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                {phoneValidationError && loginMode === 'phone' && (
                  <div className="text-sm text-red-600">{phoneValidationError}</div>
                )}
                {emailValidationError && loginMode === 'email' && (
                  <div className="text-sm text-red-600">{emailValidationError}</div>
                )}
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
